import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Wallet,
  Plus,
  Pencil,
  ToggleLeft,
  ToggleRight,
  DollarSign,
  Gift,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  aiCreditsAdminService,
  AiCreditPackage,
} from '@/features/branding/services/ai-credits.service';

export const Route = createFileRoute('/system/ai-credit-packages')({
  component: AiCreditPackagesPage,
});

function AiCreditPackagesPage() {
  const qc = useQueryClient();
  const [editPkg, setEditPkg] = useState<AiCreditPackage | null>(null);
  const [creating, setCreating] = useState(false);

  const { data: packages = [], isLoading } = useQuery({
    queryKey: ['system', 'ai-credit-packages'],
    queryFn: () => aiCreditsAdminService.listPackages(),
  });

  const toggleMutation = useMutation({
    mutationFn: (pkg: AiCreditPackage) =>
      aiCreditsAdminService.updatePackage(pkg.id, { isActive: !pkg.isActive }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['system', 'ai-credit-packages'] }),
    onError: () => toast.error('Erro ao atualizar pacote'),
  });

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Wallet className="h-6 w-6 text-indigo-400" />
          Pacotes de Créditos de IA
        </h1>
        <p className="text-gray-400 mt-1">
          Configure os pacotes de créditos de IA disponíveis para compra pelos tenants.
        </p>
      </div>

      <div className="flex justify-end">
        <Button
          onClick={() => setCreating(true)}
          className="bg-indigo-600 hover:bg-indigo-700 gap-2"
        >
          <Plus className="h-4 w-4" /> Novo Pacote
        </Button>
      </div>

      {isLoading ? (
        <p className="text-gray-400">Carregando pacotes…</p>
      ) : (
        <div className="grid gap-4">
          {packages.map((pkg) => {
            const usd = Number(pkg.usdAmount);
            const bonus = Number(pkg.bonusUsd);
            return (
              <Card key={pkg.id} className="bg-gray-900 border-gray-800">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-white text-lg">{pkg.label}</h3>
                        <Badge
                          className={pkg.isActive ? 'bg-green-700 text-white' : 'bg-gray-700 text-gray-300'}
                        >
                          {pkg.isActive ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-500 font-mono mt-0.5">{pkg.name}</p>
                      {pkg.description && (
                        <p className="text-sm text-gray-400 mt-1">{pkg.description}</p>
                      )}

                      <div className="grid grid-cols-3 gap-4 mt-4">
                        <div className="flex items-center gap-2 text-sm text-gray-300">
                          <DollarSign className="h-4 w-4 text-indigo-400 flex-shrink-0" />
                          <div>
                            <p className="text-white font-medium">
                              R$ {Number(pkg.priceBrl).toFixed(2).replace('.', ',')}
                            </p>
                            <p className="text-xs text-gray-500">preço</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-300">
                          <Wallet className="h-4 w-4 text-indigo-400 flex-shrink-0" />
                          <div>
                            <p className="text-white font-medium">US$ {usd.toFixed(2)}</p>
                            <p className="text-xs text-gray-500">créditos</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-300">
                          <Gift className="h-4 w-4 text-indigo-400 flex-shrink-0" />
                          <div>
                            <p className="text-white font-medium">
                              {bonus > 0 ? `+ US$ ${bonus.toFixed(2)}` : '—'}
                            </p>
                            <p className="text-xs text-gray-500">bônus</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 ml-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-gray-400 hover:text-white"
                        onClick={() => setEditPkg(pkg)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-gray-400 hover:text-white"
                        disabled={toggleMutation.isPending}
                        onClick={() => toggleMutation.mutate(pkg)}
                      >
                        {pkg.isActive ? (
                          <ToggleRight className="h-5 w-5 text-green-400" />
                        ) : (
                          <ToggleLeft className="h-5 w-5 text-gray-500" />
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}

          {packages.length === 0 && (
            <p className="text-gray-500 text-sm text-center py-8">
              Nenhum pacote de créditos cadastrado ainda.
            </p>
          )}
        </div>
      )}

      {creating && (
        <PackageFormDialog
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            qc.invalidateQueries({ queryKey: ['system', 'ai-credit-packages'] });
          }}
        />
      )}

      {editPkg && (
        <PackageFormDialog
          pkg={editPkg}
          onClose={() => setEditPkg(null)}
          onSaved={() => {
            setEditPkg(null);
            qc.invalidateQueries({ queryKey: ['system', 'ai-credit-packages'] });
          }}
        />
      )}
    </div>
  );
}

function PackageFormDialog({
  pkg,
  onClose,
  onSaved,
}: {
  pkg?: AiCreditPackage;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!pkg;
  const [form, setForm] = useState({
    name: pkg?.name ?? '',
    label: pkg?.label ?? '',
    description: pkg?.description ?? '',
    usdAmount: pkg?.usdAmount ?? '',
    bonusUsd: pkg?.bonusUsd ?? '0',
    priceBrl: pkg?.priceBrl ?? '',
    sortOrder: pkg?.sortOrder ?? 0,
  });

  const mutation = useMutation({
    mutationFn: () => {
      const payload = {
        name: form.name,
        label: form.label,
        description: form.description || null,
        usdAmount: Number(form.usdAmount),
        bonusUsd: Number(form.bonusUsd || 0),
        priceBrl: Number(form.priceBrl),
        sortOrder: Number(form.sortOrder),
      };
      return isEdit
        ? aiCreditsAdminService.updatePackage(pkg!.id, payload)
        : aiCreditsAdminService.createPackage(payload);
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Pacote atualizado' : 'Pacote criado');
      onSaved();
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message;
      toast.error(Array.isArray(message) ? message[0] : message || 'Erro ao salvar pacote');
    },
  });

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const canSubmit =
    form.name.trim().length > 0 &&
    form.label.trim().length > 0 &&
    form.usdAmount !== '' &&
    form.priceBrl !== '';

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="md" busy={mutation.isPending}>
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Editar Pacote' : 'Novo Pacote'}</DialogTitle>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) mutation.mutate();
          }}
        >
        <div className="space-y-4 py-2">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Slug (identificador)</Label>
              <Input
                value={form.name}
                onChange={set('name')}
                placeholder="standard"
                disabled={isEdit}
                className={isEdit ? 'opacity-50' : ''}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Nome exibido</Label>
              <Input value={form.label} onChange={set('label')} placeholder="Pacote Padrão" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Descrição</Label>
            <Textarea
              value={form.description ?? ''}
              onChange={set('description')}
              placeholder="Descreva o que este pacote oferece…"
              rows={2}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label>Créditos (US$)</Label>
              <Input
                type="number"
                value={form.usdAmount}
                onChange={set('usdAmount')}
                placeholder="25"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Bônus (US$)</Label>
              <Input
                type="number"
                value={form.bonusUsd}
                onChange={set('bonusUsd')}
                placeholder="2.5"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Preço (R$)</Label>
              <Input
                type="number"
                value={form.priceBrl}
                onChange={set('priceBrl')}
                placeholder="140"
              />
            </div>
          </div>

          <div className="space-y-1.5 sm:max-w-[50%]">
            <Label>Ordem de exibição</Label>
            <Input
              type="number"
              value={form.sortOrder}
              onChange={set('sortOrder')}
              placeholder="1"
            />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="submit"
            loading={mutation.isPending}
            disabled={!canSubmit}
            className="bg-indigo-600 hover:bg-indigo-700"
          >
            {isEdit ? 'Salvar alterações' : 'Criar pacote'}
          </Button>
        </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
